// Afab internal roles + client org roles
export type AfabRole = 'super_admin' | 'admin' | 'accounting' | 'dispatcher' | 'driver' | 'maintenance'
export type ClientRole = 'manager' | 'viewer'
export type UserRole = AfabRole | ClientRole

export type LoadLevel = 'empty' | 'quarter' | 'half' | 'full'

export type OrderStatus = 'pending' | 'accept_load' | 'load_delivered' | 'cancelled'

export type InviteStatus = 'pending' | 'accepted' | 'expired'

export interface Organization {
  id: string
  name: string
  default_pickup_location_id: string | null
  default_delivery_location_id: string | null
  created_at: string
  // joined fields
  default_pickup_location?: Location
  default_delivery_location?: Location
}

export interface Location {
  id: string
  name: string
  address: string
  organization_id: string
  created_at: string
  // HOP-70: per-location email notification config. AFAB-only fields;
  // the customer-facing ClientLocations view never reads these.
  notification_email_group?: string | null
  notification_events?: NotificationEvents
}

// Booleans for each order-status transition. Missing key = false (no email).
export type NotificationEvent = 'created' | 'accepted' | 'cancelled' | 'delivered'
export type NotificationEvents = Partial<Record<NotificationEvent, boolean>>

export const NOTIFICATION_EVENTS: NotificationEvent[] = ['created', 'accepted', 'cancelled', 'delivered']
export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  created: 'Created',
  accepted: 'Accepted',
  cancelled: 'Cancelled',
  delivered: 'Delivered',
}

// Fleet / asset registry (migration 20260606000000). Tractors + trailers as
// first-class entities; order legs, fuel usage, and maintenance attribute here.
export interface Vehicle {
  id: string
  unit_number: string
  /** Preferred label wherever this tractor surfaces; null falls back to unit_number. */
  display_name: string | null
  vin: string | null
  /** vin holds the last 6 characters off the fleet sheet, not a full 17-character VIN. */
  vin_is_partial: boolean
  plate: string | null
  base_jurisdiction: string | null
  registered_weight: number | null
  apportioned: boolean
  /** Last annual DOT inspection (YYYY-MM-DD); next due is a year later (HOP-183). */
  dot_inspected_on: string | null
  active: boolean
  notes: string | null
  created_at: string
  updated_at: string
}

export interface Trailer {
  id: string
  trailer_number: string
  /**
   * Every other number this trailer answers to — 070 for 09070. Trailers only:
   * tractors are referred to by unit number and nothing else.
   */
  aliases: string[]
  /** Preferred label wherever this trailer surfaces; null falls back to trailer_number. */
  display_name: string | null
  type: string | null
  vin: string | null
  /** vin holds a short/partial VIN rather than the full 17 characters. */
  vin_is_partial: boolean
  plate: string | null
  /** Last annual DOT inspection (YYYY-MM-DD); next due is a year later (HOP-183). */
  dot_inspected_on: string | null
  active: boolean
  notes: string | null
  created_at: string
  updated_at: string
}

export interface Profile {
  id: string
  email: string
  full_name: string
  /**
   * Every role this person holds (HOP-167). The dispatcher who schedules local and long
   * range also drives, so this is a list.
   */
  roles: UserRole[]
  /**
   * DEPRECATED (HOP-167): the most senior entry of `roles`, maintained by a database
   * trigger. Read `roles`, or the auth store's hasAnyRole(). Still present because it is
   * what login lands on and what a profile written before the column had.
   */
  role: UserRole
  organization_id: string | null  // null for Afab internal users
  disabled: boolean
  long_haul_qualified?: boolean  // admin-set; gates the driver Long-Range view
  default_vehicle_id?: string | null  // driver's assigned tractor; auto-fills on dispatch (HOP-95)
  created_at: string
  organization?: Organization
}

export interface Order {
  id: string
  /**
   * The trailer as dispatch typed it. NULL means the load had no trailer — the column
   * was NOT NULL until HOP-164, which is why TBD/MT/Empty exist in the history.
   */
  trailer_number: string | null
  trailer_id: string | null  // FK to trailers; backfilled from trailer_number (migration 20260606000000)
  pickup_location_id: string | null
  delivery_location_id: string
  load_level: LoadLevel
  status: OrderStatus
  driver_id: string | null
  organization_id: string
  requested_by: string | null
  notes: string | null
  issue_notes: string | null
  created_at: string
  accepted_at: string | null
  picked_up_at: string | null
  priority: boolean
  requires_empty_trailer: boolean
  finished_product: boolean
  completed_at: string | null
  cancelled_at: string | null
  cancelled_by: string | null
  // joined fields
  pickup_location?: Location
  delivery_location?: Location
  driver?: Profile
  organization?: Organization
  requester?: Pick<Profile, 'id' | 'full_name' | 'email'>
}

export interface Invite {
  id: string
  email: string
  role: UserRole
  organization_id: string | null
  invited_by: string
  token: string
  status: InviteStatus
  created_at: string
  expires_at: string
  last_email_attempted_at: string | null
  last_email_succeeded_at: string | null
  last_email_error: string | null
  organization?: Organization
}

// AFAB — seeded in migration 20260418000200. Empty-trailer requests use this
// location as the hardcoded pickup.
export const AFAB_ORG_ID = '00000000-0000-0000-0000-00000000afab'
export const AFAB_LOCATION_ID = '00000000-0000-0000-0000-00000afab10c'

// Helper groupings
export const AFAB_ROLES: AfabRole[] = ['super_admin', 'admin', 'accounting', 'dispatcher', 'driver', 'maintenance']
export const CLIENT_ROLES: ClientRole[] = ['manager', 'viewer']
export const ADMIN_ROLES: UserRole[] = ['super_admin', 'admin']
export const DISPATCH_ROLES: UserRole[] = ['super_admin', 'admin', 'dispatcher']
// HOP-130: who may reach the fleet registry and the maintenance record on it.
// Deliberately not DISPATCH_ROLES + maintenance as a derived array — the shop
// reads the roster and writes red tags and repairs, but never moves freight,
// so the two lists answer different questions and drift apart on purpose.
export const FLEET_ROLES: UserRole[] = ['super_admin', 'admin', 'dispatcher', 'maintenance']

export const LOAD_LEVEL_LABELS: Record<LoadLevel, string> = {
  empty: 'Empty',
  quarter: '1/4 Full',
  half: 'Half Full',
  full: 'Full',
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pending',
  accept_load: 'Accept Load',
  load_delivered: 'Load Delivered',
  cancelled: 'Cancelled',
}

// Display sort rank for the dashboard. Pending (the action queue) sits on
// top, Accepted (in flight) next, and Cancelled + Delivered share the
// "completed / reference" tail at the bottom — same rank so they sort
// chronologically against each other instead of in two separate sub-groups.
export const ORDER_STATUS_SORT_RANK: Record<OrderStatus, number> = {
  pending: 0,
  accept_load: 1,
  cancelled: 2,
  load_delivered: 2,
}

// Sort comparator shared by AdminDashboard and ClientDashboard. Pending and
// Accepted rows respect the priority flag (the urgent-now action queue);
// the combined Cancelled+Delivered tail is just newest-first by created_at.
export function compareOrders(a: Order, b: Order): number {
  const rankDiff = ORDER_STATUS_SORT_RANK[a.status] - ORDER_STATUS_SORT_RANK[b.status]
  if (rankDiff !== 0) return rankDiff
  const isActionQueue = a.status === 'pending' || a.status === 'accept_load'
  if (isActionQueue && a.priority !== b.priority) return a.priority ? -1 : 1
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
}
